<div class="kiosk">
  <!-- ═══════════════════════════════════════════ COMMON KIOSK HEADER ═══════ -->
  <header class="kiosk-header">
    <div class="brand-section">
      <img [src]="stationConfig()?.logoUrl || 'assets/logo.webp'" class="brand-logo-img" alt="JJames Smartparking" />
      <div class="brand-divider"></div>
      @if (stationConfig()?.spaceName; as name) {
      <div class="brand-logo-jku">{{ name }}</div>
      }
    </div>

    <div class="header-info">
      <div class="header-clock">
        <div class="header-time">{{ currentTime() }}</div>
        @if (phase() === 'idle') {
        @if (stationConfig()?.hotline; as hotline) {
        <div class="header-hotline">{{ i18n.t('idle.hotline') }}: {{ hotline }}</div>
        }
        }
      </div>
      @if (phase() !== 'idle') {
      <button class="header-restart-btn" (click)="cancel()" [attr.aria-label]="i18n.t('action.restart')">✕</button>
      }
    </div>
  </header>

  <!-- ═══════════════════════════════════════════ MAIN CONTENT ═══════ -->
  <main class="kiosk-body">
    @switch (phase()) {

    <!-- ═══════════════════════════════════════════ IDLE / BRANDING ═══════ -->
    @case ('idle') {
    <div class="idle-screen">
      <div class="welcome-card">
        <div class="welcome-subtitle">{{ i18n.t('idle.subtitle') }}</div>
        <h1 class="welcome-title">{{ i18n.t('idle.title') }}</h1>
        @if (stationConfig()?.spaceName; as name) {
        <div class="site-badge">{{ name }}</div>
        }
      </div>

      <div class="hero-illustration">
        <div class="hero-icon">🚗 💳</div>
        <div class="hero-text">{{ i18n.t('idle.heroText') }}</div>
      </div>

      <div class="lang-container">
        <div class="lang-title">{{ i18n.t('idle.selectLang') }}</div>
        <div class="lang-buttons">
          @for (l of availableLangs; track l.code) {
          <button class="lang-btn" [class.active]="i18n.lang() === l.code" (click)="switchLang(l.code)">
            <span class="flag-badge">{{ l.code.toUpperCase() }}</span>
            <span class="lang-label">{{ l.label }}</span>
          </button>
          }
        </div>
      </div>

      <button class="start-btn" (click)="wakeFromIdle()">
        <span>{{ i18n.t('idle.tapToStart') }}</span>
      </button>
    </div>
    }

    <!-- ═══════════════════════════════════════════ ENTRY / KEYBOARD ═══════ -->
    @case ('entry') {
    <div class="entry-screen">
      <div class="screen-title-box">
        <h1 class="screen-title">{{ i18n.t('entry.title') }}</h1>
        <div class="screen-sub">{{ i18n.t('entry.subtitle') }}</div>
      </div>
      <!-- Camera preview: visible when correcting a plate so the parker can
           see the vehicle / plate crop they are editing. -->
      @if (detection(); as d) {
      @if (d.vehicleImageUrl; as imgUrl) {
      <div class="cam-img-container cam-img-container--sm">
        <img class="cam-img" [src]="imgUrl" alt="Vehicle" />
      </div>
      }
      @if (d.plateImageUrl; as plateImgUrl) {
      <div class="plate-crop">
        <img class="plate-crop-img" [src]="plateImgUrl" alt="License plate" />
      </div>
      }
      }
      <div class="plate-wrapper">
        <div class="eu-plate">
          <div class="eu-strip">
            <span class="eu-stars">★</span>
            <span class="eu-country">A</span>
          </div>
          <div class="eu-number" [class.placeholder]="!plate()">
            {{ formattedPlate() || i18n.t('entry.platePlaceholder') }}
          </div>
        </div>
      </div>

      <!-- AUTOCOMPLETE SUGGESTIONS CHIPS -->
      @if (suggestions().length > 0) {
      <div class="suggestions-box">
        <div class="suggestions-title">{{ i18n.t('entry.suggestionsLabel') }}</div>
        <div class="suggestions-list">
          @for (s of suggestions(); track s.plate) {
          <button class="suggestion-chip" (click)="selectSuggestion(s.plate)">
            {{ s.before }}<span class="suggestion-match">{{ s.match }}</span>{{ s.after }}
          </button>
          }
        </div>
      </div>
      }

      <!-- SMART 5-ROW KIOSK KEYBOARD -->
      <div class="keyboard-box">
        @for (row of keypadRows; track $index) {
        <div class="kb-row">
          @for (key of row; track key) {
          <button class="key-btn" [class.num]="isNumberKey(key)" [class.ok]="key === 'OK'" [class.del]="key === 'DEL'"
            [disabled]="key === 'OK' && !plate()" (click)="press(key)">
            @if (key === 'DEL') {
            ⌫
            } @else if (key === 'OK') {
            {{ i18n.t('entry.search') }}
            } @else {
            {{ key }}
            }
          </button>
          }
        </div>
        }
      </div>
    </div>
    }

    <!-- ═══════════════════════════════════ DETECTION CONFIRM / CORRECT ═══ -->
    @case ('detection') {
    @if (detection(); as d) {
    <div class="detection-screen">
      <div class="screen-title-box">
        <h1 class="screen-title">{{ i18n.t('detection.title') }}</h1>
        <div class="screen-sub">{{ i18n.t('detection.subtitle') }}</div>
      </div>

      <div class="camera-card">
        <div class="cam-header">
          <span>{{ i18n.t('detection.camHeader') }}</span>
          @if (detectedAtLabel(); as capturedAt) {
          <span class="cam-status">● {{ i18n.t('detection.captured') }} {{ capturedAt }}</span>
          }
        </div>

        @if (d.vehicleImageUrl; as imgUrl) {
        <div class="cam-img-container">
          <img class="cam-img" [src]="imgUrl" alt="Vehicle" />
          <div class="cam-overlay">{{ i18n.t('detection.anprBadge') }}</div>
        </div>
        }

        @if (d.plateImageUrl; as plateImgUrl) {
        <div class="plate-crop">
          <img class="plate-crop-img" [src]="plateImgUrl" alt="License plate" />
        </div>
        }

        <div class="eu-plate eu-plate--sm">
          <div class="eu-strip">
            <span class="eu-stars">★</span>
            <span class="eu-country">A</span>
          </div>
          <div class="eu-number">{{ d.plateNumber }}</div>
        </div>
      </div>

      <div class="question-box">
        <div class="question-text">{{ i18n.t('detection.question') }}</div>
      </div>

      <div class="action-btns">
        <button class="btn-secondary" (click)="startCorrection()">
          {{ i18n.t('detection.correct') }}
        </button>
        <button class="btn-primary" (click)="confirmDetection()">
          {{ i18n.t('detection.confirm') }}
        </button>
      </div>
    </div>
    } @else {
    <div class="status-card">
      <div class="spinner"></div>
      <p class="status-text">{{ i18n.t('quoting.status') }}</p>
    </div>
    }
    }

    <!-- ═══════════════════════════════════ MODERN RECOVERY UI/UX ═══ -->
    @case ('notFound') {
    <div class="notfound-screen">
      <div class="screen-title-box">
        <h1 class="screen-title">{{ i18n.t('notFound.title') }}</h1>
        <div class="screen-sub">{{ i18n.t('notFound.subtitle') }}</div>
      </div>

      <div class="eu-plate eu-plate--sm">
        <div class="eu-strip"><span class="eu-stars">★</span><span class="eu-country">A</span></div>
        <div class="eu-number">{{ formattedPlate() }}</div>
      </div>

      <!-- VISUAL ANPR CAMERA MATCHES CARD (only rendered when real near-matches exist) -->
      @if (similarDetections().length > 0) {
      <div class="recovery-card">
        <div class="rec-badge-row">
          <span class="rec-badge">{{ i18n.t('notFound.anprHeader') }}</span>
        </div>
        <div class="similar-grid">
          @for (sim of similarDetections(); track sim.plate) {
          <div class="sim-item" (click)="selectSimilarPlate(sim)">
            @if (sim.vehicleImageUrl; as imgUrl) {
            <img class="sim-img" [src]="imgUrl" alt="Vehicle" />
            } @else {
            <div class="sim-car-icon">🚘</div>
            }
            <div class="sim-plate">{{ sim.plate }}</div>
            <div class="sim-meta">
              <span>Match {{ sim.matchScore }}</span>
              <span>⏱️ {{ sim.time }}</span>
            </div>
            <button class="btn-select-sim">{{ i18n.t('notFound.selectVehicle') }}</button>
          </div>
          }
        </div>
      </div>
      }

      <!-- MANUAL PLATE CORRECTION CARD -->
      <div class="recovery-card ocr-card">
        <div class="rec-badge-row">
          <span class="rec-badge rec-badge--gold">{{ i18n.t('notFound.ocrHeader') }}</span>
        </div>
        <div class="ocr-body">
          <div class="ocr-text">
            {{ i18n.t('notFound.ocrSub') }}
          </div>
          <button class="btn-primary-sm" (click)="editOcrMisread()">
            {{ i18n.t('notFound.editOcr') }}
          </button>
        </div>
      </div>

      <div class="action-btns">
        <button class="btn-secondary" (click)="reset()">{{ i18n.t('action.cancel') }}</button>
      </div>
    </div>
    }

    <!-- ═══════════════════════════════════════════ QUOTING ═══ -->
    @case ('quoting') {
    <div class="status-card">
      <div class="spinner"></div>
      <p class="status-text">{{ i18n.t('quoting.status') }}</p>
    </div>
    }

    <!-- ═══════════════════════════════════════════ CONFIRM AMOUNT ═══ -->
    @case ('confirm') {
    <div class="confirm-screen">
      <div class="screen-title-box">
        <h1 class="screen-title">{{ i18n.t('confirm.title') }}</h1>
      </div>

      <!-- Set only when the price moved between quote and confirm (see requoteAfterPriceChange).
           The amount below is already the new one, so the parker must be told why it changed
           before they press pay again. -->
      @if (message()) {
      <div class="confirm-notice" role="status">{{ message() }}</div>
      }

      <div class="eu-plate eu-plate--sm">
        <div class="eu-strip">
          <span class="eu-stars">★</span>
          <span class="eu-country">A</span>
        </div>
        <div class="eu-number">{{ formattedPlate() }}</div>
      </div>

      <div class="ticket-card">
        <div class="ticket-row">
          <span class="ticket-label">{{ i18n.t('confirm.plateLabel') }}</span>
          <span class="ticket-val">{{ formattedPlate() }}</span>
        </div>
        @if (quotedStay(); as stay) {
        <div class="ticket-row">
          <span class="ticket-label">{{ i18n.t('confirm.entryTime') }}</span>
          <span class="ticket-val">{{ stay.startedAt | date:'dd.MM.yyyy HH:mm' }}</span>
        </div>
        @if (stay.endedAt) {
        <div class="ticket-row">
          <span class="ticket-label">{{ i18n.t('confirm.exitTime') }}</span>
          <span class="ticket-val">{{ stay.endedAt | date:'dd.MM.yyyy HH:mm' }}</span>
        </div>
        }
        @if (stay.parkingZoneName) {
        <div class="ticket-row">
          <span class="ticket-label">{{ i18n.t('confirm.zone') }}</span>
          <span class="ticket-val">{{ stay.parkingZoneName }}</span>
        </div>
        }
        @if (stay.parkingSpaceName) {
        <div class="ticket-row">
          <span class="ticket-label">{{ i18n.t('confirm.space') }}</span>
          <span class="ticket-val">{{ stay.parkingSpaceName }}</span>
        </div>
        }
        }
        <div class="ticket-row">
          <span class="ticket-label">{{ i18n.t('confirm.tariff') }}</span>
          <span class="ticket-val tariff-highlight">{{ feeReason() || i18n.t('confirm.descriptionFallback') }}</span>
        </div>

        <div class="amount-box">
          <div class="amount-label">{{ i18n.t('confirm.amountDue') }}</div>
          <div class="amount-val">{{ amountLabel() }}</div>
        </div>
      </div>

      <!-- Credit is offered, never applied: nothing is spent unless the parker taps the button. -->
      @if (creditInCents() > 0) {
      <div class="credit-box">
        <div class="credit-info">
          <span class="credit-label">{{ i18n.t('confirm.creditAvailable') }}</span>
          <span class="credit-val">{{ creditLabel() }}</span>
        </div>
        <button class="btn-credit" (click)="payWithCredit()">{{ i18n.t('action.payCredit') }}</button>
      </div>
      }

      @if (coinsAvailable()) {
      <div class="action-btns">
        <button class="btn-coins" (click)="payWithCoins()">🪙 {{ i18n.t('action.payCoins') }}</button>
        <button class="btn-primary" (click)="pay()">{{ i18n.t('action.pay') }}</button>
      </div>
      <button class="btn-secondary" (click)="reset()">{{ i18n.t('action.cancel') }}</button>
      } @else {
      <div class="action-btns">
        <button class="btn-secondary" (click)="reset()">{{ i18n.t('action.cancel') }}</button>
        <button class="btn-primary" (click)="pay()">{{ i18n.t('action.pay') }}</button>
      </div>
      }
    </div>
    }

    <!-- ═══════════════════════════════════════════ ARMING ═══ -->
    @case ('arming') {
    <div class="status-card">
      <div class="spinner"></div>
      <!-- The reader has not been asked for a card until it acknowledges the command, so this screen
           holds until it does. The second line appears only if that takes more than a moment: a
           healthy column acks instantly, and explaining a problem that fixes itself in 400ms just
           teaches parkers to distrust the machine. -->
      <p class="status-text">
        {{ armingSlow() ? i18n.t('arming.slow') : i18n.t('arming.status') }}
      </p>
    </div>
    }

    <!-- ═══════════════════════════════════════════ TAP ═══ -->
    @case ('tap') {
    <div class="tap-screen">
      <div class="screen-title-box">
        <h1 class="screen-title">{{ i18n.t('confirm.title') }}: {{ amountLabel() }}</h1>
      </div>

      <div class="tap-card">
        <div class="tap-icon">💳</div>
        <div class="tap-text">{{ i18n.t('tap.title') }}</div>
        <div class="tap-sub">{{ i18n.t('tap.status') }}</div>
      </div>

      <button class="btn-secondary" (click)="cancel()">{{ i18n.t('action.cancel') }}</button>
    </div>
    }

    <!-- ═══════════════════════════════════════════ CREDIT / COINS ═══ -->
    @case ('redeeming') {
    <div class="status-card">
      <div class="spinner"></div>
      <p class="status-text">{{ i18n.t('credit.redeeming') }}</p>
    </div>
    }

    @case ('coinsStarting') {
    <div class="status-card">
      <div class="spinner"></div>
      <p class="status-text">{{ i18n.t('coins.starting') }}</p>
    </div>
    }

    @case ('coins') {
    <div class="coin-screen">
      <div class="screen-title-box">
        <h1 class="screen-title">{{ i18n.t('coins.title') }}</h1>
      </div>

      <div class="coin-card">
        <div class="coin-remaining-label">{{ i18n.t('coins.remaining') }}</div>
        <div class="coin-remaining">{{ coinsRemainingLabel() }}</div>
        <div class="coin-row">
          <span class="ticket-label">{{ i18n.t('coins.inserted') }}</span>
          <span class="ticket-val">{{ coinsCollectedLabel() }}</span>
        </div>
        <div class="coin-row">
          <span class="ticket-label">{{ i18n.t('confirm.amountDue') }}</span>
          <span class="ticket-val">{{ coinsTargetLabel() }}</span>
        </div>
        <div class="coin-chips">
          @for (coin of acceptedCoinLabels(); track coin) {
          <span class="coin-chip">{{ coin }}</span>
          }
        </div>
        <!-- The EMP 800 gives no change: the parker must know before the last coin goes in. -->
        <div class="coin-note">{{ i18n.t('coins.noChange') }}</div>
      </div>

      <div class="action-btns">
        <button class="btn-secondary" (click)="stopCoins('Cancel')">{{ i18n.t('action.cancel') }}</button>
        <button class="btn-primary" (click)="stopCoins('SwitchToCard')">
          {{ coinsCollected() > 0 ? i18n.t('coins.restByCard') : i18n.t('action.pay') }}
        </button>
      </div>
    </div>
    }

    @case ('coinsStopping') {
    <div class="status-card">
      <div class="spinner"></div>
      <p class="status-text">{{ i18n.t('coins.counting') }}</p>
    </div>
    }

    @case ('coinsCredited') {
    <div class="success-screen">
      <div class="success-header">
        <div class="check-badge">✓</div>
        <h1 class="success-title">{{ i18n.t('coins.creditedTitle') }}</h1>
        <div class="success-sub">{{ i18n.t('coins.creditedSub') }}</div>
      </div>

      <div class="credit-saved-card">
        <div class="amount-label">{{ i18n.t('coins.creditedAmount') }}</div>
        <div class="amount-val">{{ creditedLabel() }}</div>
      </div>

      <div class="timer-box">
        {{ i18n.t('success.autoReset') }} {{ resetSeconds() }} {{ i18n.t('success.seconds') }}
      </div>

      <button class="btn-primary" (click)="reset()">{{ i18n.t('action.done') }}</button>
    </div>
    }

    <!-- ═══════════════════════════════════════════ SUCCESS ═══ -->
    @case ('success') {
    <div class="success-screen">
      <div class="success-header">
        <div class="check-badge">✓</div>
        <h1 class="success-title">{{ i18n.t('success.title') }}</h1>
        <div class="success-sub">{{ i18n.t('success.subtitle') }}</div>
      </div>

      @if (creditedInCents() > 0) {
      <div class="credit-saved-line">{{ i18n.t('success.creditSaved') }}: + {{ creditedLabel() }}</div>
      }

      @if (paidWithCredit()) {
      <!-- No fiscal receipt for credit: that money was fiscalised in full when it came in as coins. -->
      <div class="qr-card">
        <div class="qr-title">{{ i18n.t('success.paidWithCredit') }}</div>
        <div class="amount-val">{{ redeemedLabel() }}</div>
      </div>
      } @else {
      <div class="qr-card">
        <div class="qr-title">{{ i18n.t('success.receiptTitle') }}</div>
        @if (receiptQrData(); as receipt) {
        <qrcode class="qr-img" [qrdata]="receipt" [width]="320" [margin]="2" [colorDark]="'#000000'"
          [colorLight]="'#FFFFFF'" [errorCorrectionLevel]="'M'" elementType="canvas" alt="Receipt QR code"></qrcode>
        <div class="qr-sub">{{ i18n.t('success.receiptSub') }}</div>
        } @else {
        <div class="spinner"></div>
        <div class="qr-sub">{{ i18n.t('success.generating') }}</div>
        }
      </div>
      }

      <div class="timer-box">
        {{ i18n.t('success.autoReset') }} {{ resetSeconds() }} {{ i18n.t('success.seconds') }}
      </div>

      <button class="btn-primary" (click)="reset()">{{ i18n.t('action.done') }}</button>
    </div>
    }

    <!-- ═══════════════════════════════════════════ ALREADY PAID ═══ -->
    @case ('alreadyPaid') {
    <div class="success-screen">
      <div class="success-header">
        <div class="check-badge">✓</div>
        <h1 class="success-title">{{ i18n.t('nothingDue.' + nothingDueKind() + '.title') }}</h1>
        <div class="success-sub">{{ i18n.t('nothingDue.' + nothingDueKind() + '.subtitle') }}</div>
      </div>

      <div class="eu-plate eu-plate--sm">
        <div class="eu-strip"><span class="eu-stars">★</span><span class="eu-country">A</span></div>
        <div class="eu-number">{{ formattedPlate() }}</div>
      </div>

      @if (quotedStay()) {
      <div class="ticket-card">
        @if (quotedStay(); as stay) {
        <div class="ticket-row">
          <span class="ticket-label">{{ i18n.t('confirm.entryTime') }}</span>
          <span class="ticket-val">{{ stay.startedAt | date:'dd.MM.yyyy HH:mm' }}</span>
        </div>
        @if (stay.endedAt) {
        <div class="ticket-row">
          <span class="ticket-label">{{ i18n.t('confirm.exitTime') }}</span>
          <span class="ticket-val">{{ stay.endedAt | date:'dd.MM.yyyy HH:mm' }}</span>
        </div>
        }
        @if (stay.parkingZoneName) {
        <div class="ticket-row">
          <span class="ticket-label">{{ i18n.t('confirm.zone') }}</span>
          <span class="ticket-val">{{ stay.parkingZoneName }}</span>
        </div>
        }
        @if (stay.parkingSpaceName) {
        <div class="ticket-row">
          <span class="ticket-label">{{ i18n.t('confirm.space') }}</span>
          <span class="ticket-val">{{ stay.parkingSpaceName }}</span>
        </div>
        }
        }
      </div>
      }

      <div class="timer-box">
        {{ i18n.t('success.autoReset') }} {{ resetSeconds() }} {{ i18n.t('success.seconds') }}
      </div>

      <button class="btn-primary" (click)="reset()">{{ i18n.t('action.done') }}</button>
    </div>
    }

    <!-- ═══════════════════════════════════ TERMINAL UNREACHABLE ═══ -->
    <!-- Not a declined card: the command never reached the reader, so the card was never asked for.
         Retrying is immediate — the backend frees the stay as soon as an unacknowledged attempt is
         cancelled, instead of holding it for the full five-minute window. -->
    @case ('unreachable') {
    <div class="failed-screen">
      <div class="cross-badge">!</div>
      <h1 class="screen-title">{{ i18n.t('unreachable.title') }}</h1>
      <div class="screen-sub">{{ i18n.t('unreachable.body') }}</div>

      <div class="action-btns">
        <button class="btn-secondary" (click)="reset()">{{ i18n.t('action.cancel') }}</button>
        <button class="btn-primary" (click)="quote()">{{ i18n.t('action.retry') }}</button>
      </div>

      <div class="timer-box">
        {{ i18n.t('success.autoReset') }} {{ resetSeconds() }} {{ i18n.t('success.seconds') }}
      </div>
    </div>
    }

    <!-- ═══════════════════════════════════════════ FAILED ═══ -->
    @case ('failed') {
    <div class="failed-screen">
      <div class="cross-badge">✕</div>
      <h1 class="screen-title">{{ message() ?? i18n.t('failed.title') }}</h1>

      <div class="action-btns">
        <button class="btn-secondary" (click)="reset()">{{ i18n.t('action.cancel') }}</button>
        <button class="btn-primary" (click)="quote()">{{ i18n.t('action.retry') }}</button>
      </div>

      <div class="timer-box">
        {{ i18n.t('success.autoReset') }} {{ resetSeconds() }} {{ i18n.t('success.seconds') }}
      </div>
    </div>
    }
    }
  </main>

  <!-- ═══════════════════════════════════════════ COMMON KIOSK FOOTER ═══════ -->
  <footer class="kiosk-footer">
    <div>{{ i18n.t('idle.cardNotice') }}</div>
    <div class="footer-methods">
      @if (coinsAvailable()) {
      <span class="pay-badge">🪙 {{ i18n.t('footer.coins') }}</span>
      }
      <span class="pay-badge">💳 VISA</span>
      <span class="pay-badge">💳 Mastercard</span>
      <span class="pay-badge">📱 Apple Pay</span>
    </div>
  </footer>
</div>